import {
  Activity,
  Banknote,
  BookLock,
  Bot,
  Contact,
  Inbox,
  KeyRound,
  Landmark,
  LayoutDashboard,
  MessagesSquare,
  Radio,
  ScrollText,
  Settings,
  Sparkles,
  ShieldCheck,
  Users,
  Webhook,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { AppPermission } from '@threadline/contracts';
import type { MessageKey } from '@/i18n';

export interface NavItem {
  to: string;
  /** Resolved through `t()` at render; this table is built before any provider. */
  labelKey: MessageKey;
  icon: LucideIcon;
  /**
   * The permission that opens this page. Every item has one: a menu the role
   * cannot use is worse than no menu, because it looks like a way in.
   */
  permission: AppPermission;
  /** Matches nested routes, e.g. /sessions/:id. */
  prefix?: boolean;
  shortcut?: string;
}

export interface NavGroup {
  labelKey: MessageKey;
  items: NavItem[];
}

/**
 * Grouped by what an operator is doing, not by which module implements it.
 * "Monitor" is where you live day to day; "Automate" is what you set up once;
 * "Administer" is what you touch when something is wrong.
 */
export const NAV: NavGroup[] = [
  {
    labelKey: 'nav.group.monitor',
    items: [
      { to: '/', labelKey: 'nav.overview', icon: LayoutDashboard, shortcut: 'g o', permission: 'dashboard.view' },
      { to: '/sessions', labelKey: 'nav.sessions', icon: Radio, prefix: true, shortcut: 'g s', permission: 'sessions.view' },
      { to: '/inbox', labelKey: 'nav.inbox', icon: Inbox, shortcut: 'g i', permission: 'messages.view' },
      { to: '/chats', labelKey: 'nav.chats', icon: MessagesSquare, prefix: true, permission: 'chats.view' },
      { to: '/contacts', labelKey: 'nav.contacts', icon: Contact, permission: 'contacts.view' },
    ],
  },
  {
    labelKey: 'nav.group.automate',
    items: [
      { to: '/webhooks', labelKey: 'nav.webhooks', icon: Webhook, prefix: true, permission: 'webhooks.view' },
      { to: '/keywords', labelKey: 'nav.keywords', icon: Sparkles, permission: 'keywords.manage' },
      { to: '/ai', labelKey: 'nav.ai', icon: Bot, permission: 'ai.manage' },
      { to: '/notifications', labelKey: 'nav.notifications', icon: Activity, permission: 'notifications.manage' },
      { to: '/banking/transactions', labelKey: 'banking.nav.transactions', icon: Banknote, permission: 'banking.view' },
      { to: '/banking/rules', labelKey: 'banking.nav.rules', icon: Landmark, permission: 'banking.manage' },
    ],
  },
  {
    labelKey: 'nav.group.administer',
    items: [
      { to: '/api-keys', labelKey: 'nav.apiKeys', icon: KeyRound, permission: 'apikeys.manage' },
      { to: '/users', labelKey: 'nav.users', icon: Users, permission: 'users.manage' },
      { to: '/roles', labelKey: 'nav.roles', icon: ShieldCheck, permission: 'roles.manage' },
      { to: '/logs', labelKey: 'nav.logs', icon: ScrollText, permission: 'logs.view' },
      { to: '/audit', labelKey: 'nav.audit', icon: BookLock, permission: 'audit.view' },
      { to: '/settings', labelKey: 'nav.settings', icon: Settings, permission: 'settings.manage' },
    ],
  },
];
